import { getScrollContainer, getScrollEventTarget, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/** A natural-scroll story, not a wheel/touch trap. No external animation library. */
class IncenseScrollStory extends HTMLElement {
  connectedCallback() {
    this.scenes = [...this.querySelectorAll('[data-scene]')];
    this.buttons = [...this.querySelectorAll('[data-go-to]')];
    this.navigation = this.querySelector('.incense-story__navigation');
    this.stage = this.querySelector('.incense-story__stage');
    this.motion = matchMedia('(prefers-reduced-motion: reduce), (max-height: 560px)');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.queue = () => {
      if (this.frame || !this.visible || !this.classList.contains('is-enhanced')) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    this.configure = this.configure.bind(this);
    this.motion.addEventListener('change', this.configure, options);
    scrollContainerMediaQuery.addEventListener('change', this.configure, options);
    window.addEventListener('resize', this.configure, options);
    window.addEventListener('pageshow', this.configure, options);
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-go-to], [data-skip]');
      if (!button || (!this.classList.contains('is-enhanced') && !this.classList.contains('is-mobile-compact'))) return;
      const index = button.hasAttribute('data-skip') ? this.scenes.length : Number(button.dataset.goTo);
      this.goTo(index);
    }, options);
    document.addEventListener('shopify:block:select', (event) => {
      const scene = event.target.closest('[data-scene]');
      if (scene && this.contains(scene)) this.goTo(Number(scene.dataset.scene));
    }, options);
    this.header = document.querySelector('header-component');
    this.headerObserver = new ResizeObserver(this.configure);
    if (this.header) this.headerObserver.observe(this.header);
    this.headerStateObserver = new MutationObserver(() => this.syncHeaderOffset());
    if (this.header) this.headerStateObserver.observe(this.header, { attributes: true, attributeFilter: ['sticky', 'data-sticky-state'] });
    this.configure();
    document.fonts?.ready.then(() => { if (this.isConnected) this.configure(); });
  }

  configure() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.visible = false;
    this.scrollTarget?.removeEventListener('scroll', this.queue);
    this.observer?.disconnect();
    if (!scrollContainerMediaQuery.matches && this.dataset.compactMobile === 'true' && this.scenes.length > 1) {
      this.classList.remove('is-enhanced');
      this.classList.add('is-mobile-compact');
      this.style.removeProperty('--story-track');
      this.navigation.hidden = false;
      this.selectMobileScene(Math.min(this.mobileIndex || 0, this.scenes.length - 1));
      return;
    }
    this.classList.remove('is-mobile-compact');
    if (this.motion.matches || this.scenes.length < 2) {
      this.showStatic();
      return;
    }
    // Keep the track and scene thresholds independent of header visibility.
    // Only the visible stage changes size when Vessel's native menu returns.
    this.headerHeight = this.header?.hasAttribute('sticky') ? this.header.getBoundingClientRect().height : 0;
    this.style.setProperty('--story-vh', `${window.innerHeight}px`);
    this.height = Math.max(360, window.innerHeight - this.headerHeight);
    const pace = scrollContainerMediaQuery.matches ? this.dataset.pace : (this.dataset.mobilePace || this.dataset.pace);
    this.step = window.innerHeight * (Number(pace) || 1);
    // N scenes need N - 1 transitions. A third interval previously left the
    // last scene pinned for another viewport after its transition had finished.
    this.track = window.innerHeight + this.step * (this.scenes.length - 1);
    this.style.setProperty('--story-track', `${this.track}px`);
    this.classList.add('is-enhanced');
    // Check copy against the smallest stage (menu visible), not the expanded one.
    this.style.setProperty('--story-top', `${this.headerHeight}px`);
    this.top = this.headerHeight;
    // Long merchant copy / larger accessibility text must not squeeze the photo away.
    const tooSmall = this.scenes.some(scene => {
      const content = scene.querySelector('.incense-story__content');
      const copy = scene.querySelector('.incense-story__copy');
      const styles = getComputedStyle(content);
      const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      return content.scrollHeight > this.height + 2 || (!scrollContainerMediaQuery.matches && copy.offsetHeight + padding + 180 > this.height);
    });
    if (tooSmall) { this.showStatic(); return; }
    this.syncHeaderOffset();
    this.navigation.hidden = false;
    this.scrollTarget = getScrollEventTarget();
    this.scrollTarget.addEventListener('scroll', this.queue, { passive: true });
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) this.queue();
    }, { root: getIntersectionRoot(), rootMargin: '200px 0px' });
    this.observer.observe(this);
    this.update();
  }

  syncHeaderOffset() {
    if (!this.classList.contains('is-enhanced')) return;
    const mode = this.header?.getAttribute('sticky');
    const rootTop = getIntersectionRoot()?.getBoundingClientRect().top || 0;
    const rect = this.header?.getBoundingClientRect();
    const active = this.header?.dataset.stickyState === 'active';
    const naturalVisible = rect && rect.top >= rootTop - 1 && this.header?.dataset.stickyState !== 'idle';
    // Vessel pins its header wrapper at -1px. Follow the rendered lower edge,
    // including fractional mobile heights, rather than exposing the dark track
    // between a shifted header and a stage pinned to a rounded header height.
    const visible = mode && rect && (mode === 'always' || active || naturalVisible);
    const top = visible ? Math.max(0, Math.min(this.headerHeight, rect.bottom - rootTop)) : 0;
    if (top !== this.top) {
      this.top = top;
      this.style.setProperty('--story-top', `${top}px`);
    }
  }

  showStatic() {
    this.classList.remove('is-enhanced');
    this.activeIndex = undefined;
    this.navigation.hidden = true;
    this.scenes.forEach(scene => { scene.inert = false; scene.removeAttribute('aria-hidden'); });
  }

  progress() {
    const rootTop = getIntersectionRoot()?.getBoundingClientRect().top || 0;
    return (rootTop - this.getBoundingClientRect().top) / this.step;
  }

  update() {
    this.syncHeaderOffset();
    const progress = Math.max(0, Math.min(this.scenes.length - 1, this.progress()));
    const segment = Math.floor(progress);
    // A short reading pause around each scene; the transition itself is scrubbed
    // directly by native scrolling, without delayed/time-based animations.
    const fraction = Math.max(0, Math.min(1, (progress - segment - .3) / .55));
    const position = segment + fraction * fraction * (3 - 2 * fraction);
    const index = Math.round(position);
    this.scenes.forEach((scene, i) => {
      const active = i === index;
      const offset = i - position;
      const opacity = Math.max(0, 1 - Math.abs(offset)).toFixed(3);
      const shift = `${(Math.max(-1, Math.min(1, offset)) * 48).toFixed(2)}px`;
      if (scene.dataset.opacity !== opacity) {
        scene.style.setProperty('--scene-opacity', opacity);
        scene.dataset.opacity = opacity;
        scene.classList.toggle('is-visible', Number(opacity) > 0);
      }
      if (scene.dataset.shift !== shift) {
        scene.style.setProperty('--scene-shift', shift);
        scene.dataset.shift = shift;
      }
      // If keyboard focus was on a scene link, return it to the stable scene control.
      if (!active && scene.contains(document.activeElement)) this.buttons[index]?.focus({ preventScroll: true });
      if (scene.inert !== !active || this.activeIndex === undefined) {
        scene.classList.toggle('is-active', active);
        scene.inert = !active;
        scene.setAttribute('aria-hidden', String(!active));
        this.buttons[i]?.setAttribute('aria-pressed', String(active));
      }
    });
    this.activeIndex = index;
  }

  selectMobileScene(index) {
    this.mobileIndex = index;
    this.scenes.forEach((scene, i) => {
      const active = i === index;
      scene.classList.toggle('is-active', active);
      scene.classList.toggle('is-visible', active);
      scene.style.removeProperty('--scene-opacity');
      scene.style.removeProperty('--scene-shift');
      delete scene.dataset.opacity;
      delete scene.dataset.shift;
      scene.inert = !active;
      scene.setAttribute('aria-hidden', String(!active));
      this.buttons[i]?.setAttribute('aria-pressed', String(active));
    });
    this.activeIndex = index;
  }

  goTo(index) {
    if (this.classList.contains('is-mobile-compact')) {
      if (index < this.scenes.length) this.selectMobileScene(index);
      return;
    }
    if (!this.classList.contains('is-enhanced')) return;
    const root = getScrollContainer();
    const rootTop = getIntersectionRoot()?.getBoundingClientRect().top || 0;
    const sectionTop = root.scrollTop + this.getBoundingClientRect().top - rootTop;
    // Continue goes just beyond the sticky track, not back through the story.
    const distance = index >= this.scenes.length ? this.track : index * this.step;
    root.scrollTo({ top: sectionTop + distance, behavior: 'smooth' });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.scrollTarget?.removeEventListener('scroll', this.queue);
    this.observer?.disconnect();
    this.headerObserver?.disconnect();
    this.headerStateObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('incense-scroll-story')) customElements.define('incense-scroll-story', IncenseScrollStory);
